import React from 'react';
import { motion } from 'framer-motion';
import { yourstoryInfo } from '../../data/mockData';
import { ExternalLink, Award } from 'lucide-react';
import { EditableText, EditableImage } from '../admin/InlineEditor';
import { TiltCard } from './TiltCard';
import { ScrollDepthReveal } from './ScrollDepthReveal';

const YourstorySection = () => {
  return (
    <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0a0a0c]">
      {/* Ambient Orbs */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-white/[0.05] rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-10 w-64 h-64 bg-white/[0.05] rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <ScrollDepthReveal direction="left" className="order-2 lg:order-1">
            <div className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
              <Award size={14} className="mr-2" />
              Featured on YourStory
            </div>
            
            <div className="mb-8">
              <EditableText
                pageId="home"
                sectionId="yourstory"
                fieldId="title"
                defaultValue={yourstoryInfo.title}
                as="h2"
                className="text-2xl lg:text-3xl font-bold text-white leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              />
            </div>
            
            <a
              href={yourstoryInfo.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-full hover:bg-neutral-200 transition-all duration-300 hover:shadow-lg hover:shadow-white/20 hover:scale-105"
            >
              Visit Now
              <ExternalLink size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          </ScrollDepthReveal>

          {/* YourStory Logo Card */}
          <ScrollDepthReveal direction="right" delay={0.1} className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <motion.div
              animate={{ y: [0, -10, 0], rotateZ: [-1.5, 1.5, -1.5] }}
              transition={{ y: { duration: 6, repeat: Infinity, ease: 'easeInOut' }, rotateZ: { duration: 8, repeat: Infinity, ease: 'easeInOut' } }}
            >
              <TiltCard maxTilt={14} testId="yourstory-tilt-card" className="group">
                {/* Glow Effect */}
                <div className="absolute -inset-2 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

                {/* Glass Card */}
                <div className="relative bg-white/[0.04] backdrop-blur-2xl rounded-3xl p-8 border border-white/10 shadow-2xl shadow-black/50">
                  {/* Glass Reflection */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-3xl" />

                  {/* Logo Container — kept white so the logo graphic stays legible */}
                  <div className="relative bg-white rounded-2xl p-4 shadow-inner border border-neutral-200" style={{ transform: 'translateZ(35px)' }}>
                    <EditableImage
                      pageId="home"
                      sectionId="yourstory"
                      fieldId="logo"
                      defaultSrc={yourstoryInfo.logo}
                      alt="YourStory"
                      className="max-w-[220px] w-full h-auto"
                    />
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-3 -right-3 w-6 h-6 bg-white/30 rounded-full" />
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-white/20 rounded-full" />
                </div>
              </TiltCard>
            </motion.div>
          </ScrollDepthReveal>
        </div>
      </div>
    </section>
  );
};

export default YourstorySection;
